import { WorkbenchRow } from '../../models/workbench-row.model'

/**
 * Clearwater quick filters. These replace Positions 1.0's _Only Differences_ / _Only Incorrect_.
 *
 * Clearwater has no /flex-positions endpoint, so its grid stays on the client-side row model and these
 * stay client-side predicates. They are the ONLY external filter left.
 *
 * The status chips and the match-count cross-filter both used to live here as in-memory predicates
 * over the loaded rows. Neither does any more:
 *
 *  - The chips are a second face on the real `matchStatus` column filter — see
 *    {@link ./match-status-chips.ts}.
 *  - The cross-filter is a `rowId` set filter on the /query request — see `query-request.builder.ts`.
 *
 * Both were removed rather than kept as a fallback. An in-memory predicate can only ever match rows in
 * the currently-loaded block, so on a server-side grid it silently presents a rolling window's matches
 * as the whole result; and holding the same restriction in two places let ag-Grid's own filter state
 * and the toolbar's shadow copy drift apart.
 *
 * _Breaks Only_ is gone for a different reason: there was never any break data behind it. The row
 * adapter hardcoded `{high: 0, medium: 0}` on every row, so the predicate below matched NOTHING and
 * the toggle emptied the grid — a false negative indistinguishable from "this account has no breaks".
 * The field is out of the PVAL-26116 contract and has no endpoint to come from, so the filter went
 * with the column rather than staying as a control that can only mislead.
 */
export interface QuickFilterState {
  /** Rows with a non-zero Custody or Trading unit difference. */
  differencesOnly: boolean
}

/** Composite external-filter model. Client-side dimensions only — see the note above. */
export interface WorkbenchExternalFilterModel {
  quick: QuickFilterState | null
}

const quickRestricts = (quick: QuickFilterState): boolean =>
  quick.differencesOnly

/**
 * Whether the external filter can exclude anything. This tests the *state*, not merely its presence:
 * a source that owns quick filters still reports inactive while they are all off, so ag-Grid skips
 * `doesExternalFilterPass` for every row. That matters because Differences Only walks the match graph
 * per row, which is far too expensive to pay for when no filter is actually applied.
 */
export const externalFilterActive = (
  model: WorkbenchExternalFilterModel,
): boolean => model.quick != null && quickRestricts(model.quick)

/**
 * Evaluate the composite external filter for one row.
 *
 * `hasUnitDifference` is injected rather than computed here because the difference depends on the live
 * match graph, which this pure predicate has no access to.
 */
export function passesExternalFilter(
  row: WorkbenchRow,
  model: WorkbenchExternalFilterModel,
  hasUnitDifference: (row: WorkbenchRow) => boolean,
): boolean {
  if (model.quick) {
    if (model.quick.differencesOnly && !hasUnitDifference(row)) return false
  }

  return true
}
